import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Badge,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Divider,
  Table,
  Tbody,
  Tr,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Select,
  Button,
  ButtonGroup
} from '@chakra-ui/react';
import { ResponsiveBar } from '@nivo/bar';
import { FiUser, FiTrendingUp, FiClock, FiTarget, FiActivity, FiCamera, FiCpu, FiBarChart2 } from 'react-icons/fi';
import axios from 'axios';

const UserDetailedStats = ({ user, stats }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const [productivityData, setProductivityData] = useState(null);
  const [productivityPeriod, setProductivityPeriod] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monthGroupBy, setMonthGroupBy] = useState('day'); // Para período mensal: 'day' ou 'week'
  const [selectedMonth, setSelectedMonth] = useState(''); // Mês selecionado no formato YYYY-MM
  const [loadingProductivity, setLoadingProductivity] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Buscar informações do usuário atual para verificar permissões
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Erro ao buscar usuário atual:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Definir datas padrão quando o componente carrega
  useEffect(() => {
    if (!startDate || !endDate) {
      const today = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      
      setEndDate(today.toISOString().split('T')[0]);
      setStartDate(weekAgo.toISOString().split('T')[0]);
    }
  }, []);

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Função para buscar dados de produtividade
  const fetchProductivityData = async () => {
    if (!user?.id || !currentUser) return;
    
    // Verificar permissões antes de fazer a requisição
    if (currentUser.role < 3 && currentUser.id !== user.id) {
      console.warn('Usuário não tem permissão para ver dados de produtividade de outros usuários');
      return;
    }
    
    setLoadingProductivity(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        period: productivityPeriod
      };
      
      // Para período diário, usar as datas selecionadas
      if (productivityPeriod === 'daily') {
        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }
      }
      
      // Adicionar parâmetro monthGroupBy apenas para período mensal
      if (productivityPeriod === 'monthly') {
        params.monthGroupBy = monthGroupBy;
        // Adicionar mês selecionado se especificado
        if (selectedMonth) {
          params.month = selectedMonth;
        }
      }
      
      const response = await axios.get(`/api/users/${user.id}/productivity-data`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setProductivityData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados de produtividade:', error);
      if (error.response?.status === 403) {
        console.warn('Acesso negado para dados de produtividade');
      }
    } finally {
      setLoadingProductivity(false);
    }
  };

  // Buscar dados quando o usuário ou período mudar
  useEffect(() => {
    fetchProductivityData();
  }, [user?.id, productivityPeriod, startDate, endDate, monthGroupBy, selectedMonth, currentUser]);

  // Preparar dados para o gráfico
  const prepareChartData = () => {
    if (!productivityData?.data) return [];
    
    return productivityData.data.map(item => ({
      label: item.label,
      date: item.date,
      Confirmadas: item.confirmed,
      Rejeitadas: item.rejected,
      Pendentes: item.pending,
      Total: item.total,
      Lotes: item.batches
    }));
  };

  // Configuração das cores personalizadas
  const customColors = ['#48bb78', '#f56565', '#ecdf25ff']; // Verde, Vermelho, Amarelo

  // Configuração do gráfico
  const chartConfig = {
    data: prepareChartData(),
    keys: ['Confirmadas', 'Rejeitadas', 'Pendentes'], // Sempre essas 3 barras lado a lado
    indexBy: 'label',
    margin: { top: 50, right: 60, bottom: 80, left: 60 },
    padding: 0.3,
    valueScale: { type: 'linear' },
    indexScale: { type: 'band', round: true },
    colors: customColors, // Cores personalizadas
    groupMode: 'grouped', // Barras lado a lado, não empilhadas
    borderColor: {
      from: 'color',
      modifiers: [['darker', 1.6]]
    },
    axisTop: null,
    axisRight: null,
    axisBottom: {
      tickSize: 5,
      tickPadding: 5,
      tickRotation: productivityData?.data?.length > 7 ? -45 : 0,
      legend: getPeriodLabel(),
      legendPosition: 'middle',
      legendOffset: 32
    },
    axisLeft: {
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: 'Quantidade',
      legendPosition: 'middle',
      legendOffset: -40
    },
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    labelTextColor: {
      from: 'color',
      modifiers: [['darker', 1.6]]
    },
    legends: [
      {
        dataFrom: 'keys',
        anchor: 'bottom-right',
        direction: 'column',
        justify: false,
        translateX: 120,
        translateY: 0,
        itemsSpacing: 2,
        itemWidth: 100,
        itemHeight: 20,
        itemDirection: 'left-to-right',
        itemOpacity: 0.85,
        symbolSize: 20,
        effects: [
          {
            on: 'hover',
            style: {
              itemOpacity: 1
            }
          }
        ]
      }
    ],
    role: 'application',
    ariaLabel: 'Gráfico de produtividade do usuário',
    barAriaLabel: function(e) {
      return e.id + ": " + e.formattedValue + " no período: " + e.indexValue;
    }
  };

  // Função para obter o label do período
  function getPeriodLabel() {
    switch (productivityPeriod) {
      case 'daily': return 'Dias';
      case 'weekly': return 'Dias da Semana';
      case 'monthly': return monthGroupBy === 'day' ? 'Dias do Mês' : 'Semanas do Mês';
      default: return 'Período';
    }
  }

  // Função para definir período rápido
  const setQuickPeriod = (days) => {
    const today = new Date();
    const startDay = new Date();
    startDay.setDate(today.getDate() - days);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(startDay.toISOString().split('T')[0]);
  };

  // Função para gerar opções de mês (últimos 12 meses)
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      
      options.push({ value, label });
    }
    
    return options;
  };

  if (!user || !stats) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertTitle>Nenhum usuário selecionado</AlertTitle>
        <AlertDescription>Selecione um usuário para ver suas estatísticas detalhadas.</AlertDescription>
      </Alert>
    );
  }

  // Verificar se o usuário atual tem permissão para ver as estatísticas detalhadas
  if (currentUser && currentUser.role < 3 && currentUser.id !== user.id) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertTitle>Acesso Restrito</AlertTitle>
        <AlertDescription>
          Apenas usuários de nível 3 ou superior podem visualizar estatísticas detalhadas de outros usuários.
        </AlertDescription>
      </Alert>
    );
  }

  const { user: userData, stats: userStats } = stats;
  const infractions = userStats.infractions || { confirmed: 0, rejected: 0, pending: 0, total: 0 };
  const batches = userStats.batches || { completed: 0, inProgress: 0, assigned: 0, total: 0 };
  const productivity = userStats.productivity || { 
    today: { infractions: 0, batches: 0 }, 
    week: { infractions: 0, batches: 0 }, 
    month: { infractions: 0, batches: 0 },
    yesterday: { infractions: 0, batches: 0 }
  };
  const performance = userStats.performance || { 
    accuracy: 0, 
    avgTimePerInfraction: 0, 
    consistency: 0, 
    ranking: 999, 
    totalUsers: 1,
    totalReviewed: 0
  };
  const byAlgorithm = userStats.byAlgorithm || [];
  const byCamera = userStats.byCamera || [];
  const recentActivity = userStats.recentActivity || [];
  const status = userStats.status || { isActive: false, lastActivity: null };
  const recentBatches = userStats.recentBatches || [];

  const totalAnalyzed = infractions.confirmed + infractions.rejected;
  const confirmationRate = totalAnalyzed > 0 ? ((infractions.confirmed / totalAnalyzed) * 100).toFixed(1) : 0;
  const rejectionRate = totalAnalyzed > 0 ? ((infractions.rejected / totalAnalyzed) * 100).toFixed(1) : 0;

  return (
    <VStack spacing={6} align="stretch" p={4}>
      {/* Header do Usuário */}
      <Card bg={bgColor}>
        <CardHeader>
          <HStack spacing={4}>
            <Box p={3} bg="blue.500" borderRadius="full">
              <FiUser color="white" size={24} />
            </Box>
            <VStack align="start" spacing={1}>
              <Heading size="lg">{userData?.name || userData?.username || user.username}</Heading>
              <HStack>
                <Badge colorScheme={status.isActive ? "green" : "gray"}>
                  {status.isActive ? "Ativo" : "Inativo"}
                </Badge>
                <Badge colorScheme="blue">
                  Nível {userData?.role || user.role}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Última atividade: {formatDate(status.lastActivity)}
              </Text>
            </VStack>
          </HStack>
        </CardHeader>
      </Card>

      {/* Estatísticas Principais */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        <Card bg={bgColor}>
          <CardBody>
            <Stat>
              <StatLabel>Total de Infrações</StatLabel>
              <StatNumber>{infractions.total}</StatNumber>
              <StatHelpText>
                {infractions.confirmed} confirmadas, {infractions.rejected} rejeitadas
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor}>
          <CardBody>
            <Stat>
              <StatLabel>Total de Lotes</StatLabel>
              <StatNumber>{batches.total}</StatNumber>
              <StatHelpText>
                {batches.completed} concluídos, {batches.inProgress} em progresso
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor}>
          <CardBody>
            <Stat>
              <StatLabel>Taxa de Confirmação</StatLabel>
              <StatNumber>{confirmationRate}%</StatNumber>
              <StatHelpText>
                De {totalAnalyzed} analisadas
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Gráfico de Produtividade */}
      {currentUser && (currentUser.role >= 3 || currentUser.id === user.id) && (
        <Card bg={bgColor}>
          <CardHeader>
            <HStack justify="space-between" align="center" wrap="wrap">
              <HStack>
                <FiBarChart2 />
                <Heading size="md">Produtividade</Heading>
              </HStack>
              <HStack spacing={4} wrap="wrap">
                <HStack>
                  <Text fontSize="sm">Período:</Text>
                  <ButtonGroup size="sm" isAttached variant="outline">
                    <Button 
                      colorScheme={productivityPeriod === 'daily' ? 'blue' : 'gray'}
                      onClick={() => setProductivityPeriod('daily')}
                    >
                      Diário
                    </Button>
                    <Button 
                      colorScheme={productivityPeriod === 'weekly' ? 'blue' : 'gray'}
                      onClick={() => setProductivityPeriod('weekly')}
                    >
                      Semanal
                    </Button>
                    <Button 
                      colorScheme={productivityPeriod === 'monthly' ? 'blue' : 'gray'}
                      onClick={() => setProductivityPeriod('monthly')}
                    >
                      Mensal
                    </Button>
                  </ButtonGroup>
                </HStack>
                {productivityPeriod === 'daily' && (
                  <VStack spacing={2}>
                    <HStack spacing={2}>
                      <VStack spacing={1}>
                        <Text fontSize="xs">Data Inicial:</Text>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '4px',
                            fontSize: '14px',
                            width: '130px'
                          }}
                        />
                      </VStack>
                      <VStack spacing={1}>
                        <Text fontSize="xs">Data Final:</Text>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '4px',
                            fontSize: '14px',
                            width: '130px'
                          }}
                        />
                      </VStack>
                    </HStack>
                    <HStack spacing={1}>
                      <Text fontSize="xs">Rápido:</Text>
                      <ButtonGroup size="xs" variant="outline">
                        <Button onClick={() => setQuickPeriod(7)}>7d</Button>
                        <Button onClick={() => setQuickPeriod(15)}>15d</Button>
                        <Button onClick={() => setQuickPeriod(30)}>30d</Button>
                        <Button onClick={() => setQuickPeriod(60)}>60d</Button>
                      </ButtonGroup>
                    </HStack>
                  </VStack>
                )}
                {productivityPeriod === 'monthly' && (
                  <HStack>
                    <Text fontSize="sm">Agrupar por:</Text>
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <Button 
                        colorScheme={monthGroupBy === 'day' ? 'blue' : 'gray'}
                        onClick={() => setMonthGroupBy('day')}
                      >
                        Dia
                      </Button>
                      <Button 
                        colorScheme={monthGroupBy === 'week' ? 'blue' : 'gray'}
                        onClick={() => setMonthGroupBy('week')}
                      >
                        Semana
                      </Button>
                    </ButtonGroup>
                  </HStack>
                )}
                {productivityPeriod === 'monthly' && (
                  <HStack>
                    <Text fontSize="sm">Mês:</Text>
                    <Select 
                      size="sm" 
                      width="160px" 
                      value={selectedMonth} 
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      placeholder="Mês atual"
                    >
                      {generateMonthOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                )}
              </HStack>
            </HStack>
          </CardHeader>
          <CardBody>
            {loadingProductivity ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <Spinner size="lg" />
              </Box>
            ) : productivityData ? (
              <VStack spacing={4} align="stretch">
                {/* Totais do período */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Box p={3} bg="green.50" borderRadius="md" textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      {productivityData.totals.confirmed}
                    </Text>
                    <Text fontSize="sm" color="green.500">Confirmadas</Text>
                  </Box>
                  <Box p={3} bg="red.50" borderRadius="md" textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="red.600">
                      {productivityData.totals.rejected}
                    </Text>
                    <Text fontSize="sm" color="red.500">Rejeitadas</Text>
                  </Box>
                  <Box p={3} bg="yellow.50" borderRadius="md" textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="yellow.600">
                      {productivityData.totals.pending}
                    </Text>
                    <Text fontSize="sm" color="yellow.500">Pendentes</Text>
                  </Box>
                  <Box p={3} bg="blue.50" borderRadius="md" textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      {productivityData.totals.total}
                    </Text>
                    <Text fontSize="sm" color="blue.500">Total Analisadas</Text>
                  </Box>
                </SimpleGrid>

                {/* Gráfico */}
                <Box height="400px" width="100%">
                  {chartConfig.data.length > 0 ? (
                    <ResponsiveBar {...chartConfig} />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Text color="gray.500">Nenhum dado encontrado para o período selecionado</Text>
                    </Box>
                  )}
                </Box>
              </VStack>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <Text color="gray.500">Erro ao carregar dados de produtividade</Text>
              </Box>
            )}
          </CardBody>
        </Card>
      )}

      {/* Performance */}
      <Card bg={bgColor}>
        <CardHeader>
          <HStack>
            <FiTarget />
            <Heading size="md">Performance</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <VStack>
              <Text fontWeight="bold">Precisão</Text>
              <Text fontSize="2xl">{performance.accuracy}%</Text>
              <Progress value={performance.accuracy} colorScheme="green" width="100%" />
            </VStack>
            <VStack>
              <Text fontWeight="bold">Tempo Médio</Text>
              <Text fontSize="2xl">{Math.round(performance.avgTimePerInfraction / 1000)}s</Text>
              <Text fontSize="sm" color="gray.500">por infração</Text>
            </VStack>
            <VStack>
              <Text fontWeight="bold">Consistência</Text>
              <Text fontSize="2xl">{performance.consistency}%</Text>
              <Progress value={performance.consistency} colorScheme="blue" width="100%" />
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Estatísticas por Algoritmo e Câmera */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {/* Por Algoritmo */}
        <Card bg={bgColor}>
          <CardHeader>
            <HStack>
              <FiCpu />
              <Heading size="md">Por Algoritmo</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {byAlgorithm.length > 0 ? (
              <Table variant="simple" size="sm">
                <Tbody>
                  {byAlgorithm.slice(0, 5).map((alg, index) => (
                    <Tr key={index}>
                      <Td>{alg.algorithmName}</Td>
                      <Td isNumeric>{alg.total}</Td>
                      <Td isNumeric>
                        <Badge colorScheme="green" mr={1}>{alg.confirmed}</Badge>
                        <Badge colorScheme="red">{alg.rejected}</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text color="gray.500">Nenhuma análise por algoritmo</Text>
            )}
          </CardBody>
        </Card>

        {/* Por Câmera */}
        <Card bg={bgColor}>
          <CardHeader>
            <HStack>
              <FiCamera />
              <Heading size="md">Por Câmera</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {byCamera.length > 0 ? (
              <Table variant="simple" size="sm">
                <Tbody>
                  {byCamera.slice(0, 5).map((cam, index) => (
                    <Tr key={index}>
                      <Td>{cam.cameraName}</Td>
                      <Td isNumeric>{cam.total}</Td>
                      <Td isNumeric>
                        <Badge colorScheme="green" mr={1}>{cam.confirmed}</Badge>
                        <Badge colorScheme="red">{cam.rejected}</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text color="gray.500">Nenhuma análise por câmera</Text>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Atividade Recente e Lotes Recentes */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {/* Atividade Recente */}
        <Card bg={bgColor}>
          <CardHeader>
            <HStack>
              <FiActivity />
              <Heading size="md">Atividade Recente</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {recentActivity.length > 0 ? (
              <VStack spacing={3} align="stretch" maxH="500px" overflowY="auto" pr={2}>
                {recentActivity.map((activity, index) => (
                  <Box key={index} p={3} borderRadius="md" bg="gray.50" _dark={{ bg: "gray.700" }}>
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{activity.description}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {formatDate(activity.timestamp)}
                        </Text>
                      </VStack>
                      <Badge 
                        colorScheme={
                          activity.type.includes('confirmed') ? 'green' : 
                          activity.type.includes('rejected') ? 'red' : 'blue'
                        }
                      >
                        {activity.type.replace('_', ' ')}
                      </Badge>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="gray.500">Nenhuma atividade recente</Text>
            )}
          </CardBody>
        </Card>

        {/* Lotes Recentes */}
        <Card bg={bgColor}>
          <CardHeader>
            <HStack>
              <FiClock />
              <Heading size="md">Lotes Recentes</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {recentBatches.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {recentBatches.slice(0, 5).map((batch, index) => (
                  <Box key={index} p={3} borderRadius="md" bg="gray.50" _dark={{ bg: "gray.700" }}>
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Lote #{batch.id}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {batch.analyzed_infractions}/{batch.total_infractions} analisadas
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          Criado: {formatDate(batch.created_at)}
                        </Text>
                      </VStack>
                      <VStack spacing={1}>
                        <Badge 
                          colorScheme={
                            batch.status === 'completed' ? 'green' : 
                            batch.status === 'in_progress' ? 'blue' : 'gray'
                          }
                        >
                          {batch.status}
                        </Badge>
                        {batch.status === 'in_progress' && (
                          <Progress 
                            value={(batch.analyzed_infractions / batch.total_infractions) * 100} 
                            size="sm" 
                            width="60px"
                          />
                        )}
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="gray.500">Nenhum lote recente</Text>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </VStack>
  );
};

export default UserDetailedStats;